'use client'

import { useEffect, useMemo, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { classNames, formatDateNormal, formatImageURL, formatNumber, getBackgroundColorStyles, getVendorPrimaryColor, isLoyaltyActive } from "@/utils/util"
import { ArrowDownUp, CircleCheckBig, CircleHelp, CirclePlus, CircleX, LogIn, Medal, MessageCircleX, PenLine, SendHorizonal, Star, Trash, X } from "lucide-react"
import { Button } from "@/components/elements/Button"
import Avatar from "boring-avatars"
import { navigateUrl, sendClientRequest } from "@/utils/client-request"
import TextEditorMininal from "@/components/elements/TextEditorMinimal"
import EmptyState from "@/components/elements/EmptyState"
import ImageCarousel from "@/components/blocks/ImageCarousel"
import { useCart, useProductReviews, type ReviewFilterId } from "@/lib/commerce/hooks"

interface ReviewProps {
  product: any
  vendor: any
  reviews: {
    counts: { rating: number; count: number }[]
    data: any[]
    totalCount: number
    average: number
    userReviews?: any[]
  }
  canReview?: boolean
}

export default function Review({ product, vendor, reviews: initialReviews, canReview = false }: ReviewProps) {
  const pathname = usePathname()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { loyaltySummary } = useCart()
  const [publicBasePoints, setPublicBasePoints] = useState(0)
  const reviewRewardPoints = isLoyaltyActive(vendor) ? (loyaltySummary?.currentTier?.reviewRewardPoints || 0) : 0
  const reviewRewardCapCount = loyaltySummary?.reviewRewardCapCount || 0
  const reviewRewardCapWindowDays = loyaltySummary?.reviewRewardCapWindowDays || 0
  const reviewRewardCapped = reviewRewardPoints > 0 && reviewRewardCapCount > 0 && reviewRewardCapWindowDays > 0 && (loyaltySummary?.reviewRewardUsedCount || 0) >= reviewRewardCapCount

  useEffect(() => {
    if (canReview || !isLoyaltyActive(vendor)) return
    let cancelled = false
    sendClientRequest({ url: '/loyalty/tiers', auth: false, cache: true, headers: { Vendor: vendor?.id } }).then(({ data }: any) => {
      if (cancelled || !data?.tiers?.length) return
      const sorted = [...data.tiers].sort((a: any, b: any) => a.minAnnualSpend - b.minAnnualSpend)
      setPublicBasePoints(sorted[0]?.reviewRewardPoints || 0)
    }).catch(() => { })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview, vendor?.id])

  const reviewPointsChip = reviewRewardPoints > 0 && (
    reviewRewardCapped ? (
      <span className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-500 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap tabular-nums">
        <Medal className="w-3.5 h-3.5" />
        Онооны лимит хүрсэн
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap tabular-nums">
        <Medal className="w-3.5 h-3.5" />
        +{formatNumber(reviewRewardPoints)} оноо
      </span>
    )
  )

  const {
    reviews,
    deletedReview, pendingReview, publishedReview, hasAnyReview,
    totalCount, photoCount, fiveStarCount,
    getFilteredReviews,
    content, setContent,
    rating, setRating,
    images,
    btnLoading,
    loadingImages,
    postReview,
    uploadImages,
    removeImage,
  } = useProductReviews({ product, initialReviews })

  const [isCarouselOpen, setIsCarouselOpen] = useState(false)
  const [carouselImages, setCarouselImages] = useState<string[]>([])
  const [activeImage, setActiveImage] = useState<string>('')
  const [activeFilter, setActiveFilter] = useState<ReviewFilterId>('all')
  const [composerOpen, setComposerOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [sortNewest, setSortNewest] = useState(true)

  const primaryColor = getVendorPrimaryColor(vendor)
  const { className: backgroundColorClass, style: backgroundColorStyle } = getBackgroundColorStyles(primaryColor)

  const filteredReviews = useMemo(() => getFilteredReviews(activeFilter, sortNewest), [reviews.data, activeFilter, sortNewest])
  const visibleReviews = showAll ? filteredReviews : filteredReviews.slice(0, 5)
  const hasMore = filteredReviews.length > 5

  if (!product) return <></>

  const handleImages = async (e: any) => {
    await uploadImages(e.target.files)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const openImageInCarousel = (imgs: string[], clickedImage: string) => {
    setCarouselImages(imgs)
    setActiveImage(clickedImage)
    setIsCarouselOpen(true)
  }

  const filters: { id: ReviewFilterId, label: React.ReactNode }[] = [
    { id: 'all', label: `Бүгд (${totalCount})` },
    { id: '5', label: <span className="inline-flex items-center gap-1">5<Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />({fiveStarCount})</span> },
    { id: 'photo', label: `Зурагтай (${photoCount})` },
  ]

  const composer = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {[0, 1, 2, 3, 4].map((r) => (
            <Star key={r} onClick={() => setRating(r + 1)} className={classNames(rating > r ? `text-yellow-500 fill-yellow-500` : 'fill-muted-foreground/10 text-muted-foreground/10', 'size-6 shrink-0 cursor-pointer')} />
          ))}
        </div>
        <input type="file" accept="image/*" multiple ref={fileInputRef} className="hidden" onChange={handleImages} />
        <Button variant="outline" icon={CirclePlus} isLoading={loadingImages} onClick={() => fileInputRef.current?.click()}>
          Зураг
        </Button>
      </div>
      <div className="py-3 px-4 border border-border rounded-lg min-h-32 [&_.ProseMirror]:min-h-24 [&_.ProseMirror]:outline-none">
        <TextEditorMininal content={content} setContent={setContent} />
      </div>
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map((image, index) => (
            <div key={index} className="relative aspect-square">
              <img src={formatImageURL(image)} alt={`Review image ${index + 1}`} className="w-full h-full object-cover rounded-md bg-muted cursor-pointer" onClick={() => openImageInCarousel(images, image)} />
              <button type="button" onClick={() => removeImage(image)} className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80">
                <Trash className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end">
        <Button icon={SendHorizonal} isLoading={btnLoading} onClick={postReview} color={primaryColor}>
          Илгээх
        </Button>
      </div>
    </div>
  )

  const renderSummaryAside = (
    <aside className="bg-white rounded-2xl border border-border p-6 self-start lg:sticky lg:top-24">
      <div className="flex items-baseline gap-2">
        <span className="text-[44px] font-semibold tracking-[-0.02em] leading-none">
          {reviews.average ? Number(reviews.average).toFixed(1) : '0.0'}
        </span>
        <span className="text-sm text-muted-foreground tabular-nums">/ 5.0</span>
      </div>
      <div className="mt-2 flex items-center gap-0.5">
        {[0, 1, 2, 3, 4].map((r) => (
          <Star key={r} className={classNames(Math.round(reviews.average ?? 0) > r ? `text-yellow-500 fill-yellow-500` : 'fill-muted-foreground/10 text-muted-foreground/10', 'size-4 shrink-0')} />
        ))}
      </div>
      <div className="mt-1.5 text-xs text-muted-foreground">
        Нийт <span className="font-medium text-foreground">{totalCount}</span> сэтгэгдэл
      </div>

      <div className="mt-5 space-y-1.5">
        {reviews.counts.slice().reverse().map((c) => {
          const pct = totalCount > 0 ? Math.round((c.count / totalCount) * 100) : 0
          return (
            <div key={c.rating} className="grid grid-cols-[14px_1fr_72px] items-center gap-2.5 text-xs">
              <span className="font-medium text-muted-foreground tabular-nums">{c.rating}</span>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className={`h-full rounded-full transition-all ${backgroundColorClass}`} style={{ width: `${pct}%`, ...backgroundColorStyle }} />
              </div>
              <div className="text-right text-muted-foreground tabular-nums whitespace-nowrap">
                <span className="font-medium text-foreground">{pct}%</span>
                <span className="ml-1 text-[11px]">({c.count})</span>
              </div>
            </div>
          )
        })}
      </div>

    </aside>
  )

  const renderComposerSection = (
    canReview ? (
      hasAnyReview ? (
        <div className="mt-6 bg-white rounded-2xl border border-border p-5 sm:p-6">
          <UserReviewStatus publishedReview={publishedReview} pendingReview={pendingReview} deletedReview={deletedReview} composerOpen={composerOpen} setComposerOpen={setComposerOpen} composer={composer} openImageInCarousel={openImageInCarousel} />
        </div>
      ) : (
        composerOpen ? (
          <div className="mt-6 bg-white rounded-2xl border border-border p-5 sm:p-6">
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <div className="text-base font-semibold text-foreground">Сэтгэгдэл бичих</div>
                {reviewPointsChip}
              </div>
              <button type="button" onClick={() => setComposerOpen(false)} className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            {composer}
          </div>
        ) : (
          <div className="mt-6 bg-white rounded-2xl border border-border p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base text-foreground">Энэ бүтээгдэхүүний талаар сэтгэгдэл үлдээх үү?</h3>
                {reviewPointsChip}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                {reviewRewardCapped
                  ? `Та ${reviewRewardCapWindowDays} хоногт ${reviewRewardCapCount} сэтгэгдэл бичиж оноо цуглуулах лимитэд хүрсэн байна. Таны энэ сэтгэгдэлд оноо олгогдохгүй ч бусад худалдан авагчдад худалдан авалт хийхэд нь туслаарай.`
                  : reviewRewardPoints > 0
                    ? `Үнэлгээ өгч бусад худалдан авагчдад туслаад, ${formatNumber(reviewRewardPoints)} оноо цуглуулаарай.`
                    : 'Хэрэглэсэн бүтээгдэхүүнийхээ талаар үнэлгээ өгч, бусад худалдан авагчдад туслаарай.'}
              </p>
            </div>
            <Button icon={PenLine} onClick={() => setComposerOpen(true)} color={primaryColor}>
              Сэтгэгдэл бичих
            </Button>
          </div>
        )
      )
    ) : (
      <div className="mt-6 bg-white rounded-2xl border border-border p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-base text-foreground">Нэвтэрсэн хэрэглэгч үнэлгээ өгөх боломжтой</h3>
            {publicBasePoints > 0 && (
              <span className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap tabular-nums">
                <Medal className="w-3.5 h-3.5" />
                +{formatNumber(publicBasePoints)} оноо
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mt-1">
            {publicBasePoints > 0
              ? `Нэвтэрч сэтгэгдэл, үнэлгээ үлдээгээд ${formatNumber(publicBasePoints)} оноо цуглуулж, бусад хэрэглэгчдэд зөв сонголт хийхэд нь туслаарай.`
              : 'Хэрэглэсэн бүтээгдэхүүнийхээ талаар сэтгэгдэл, үнэлгээ үлдээж бусад хэрэглэгчдэд зөв сонголт хийхэд нь туслаарай.'}
          </p>
        </div>
        <Button tag="a" href={navigateUrl(`/login?url=/product/${product.customId}`, pathname)} icon={LogIn} variant="outline">
          Нэвтрэх
        </Button>
      </div>
    )
  )

  return (
    <div>
      <ImageCarousel images={carouselImages} initialImage={activeImage} isOpen={isCarouselOpen} onClose={() => setIsCarouselOpen(false)} productName={product.name} vendor={vendor} />

      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-base sm:text-2xl font-semibold leading-8 tracking-[.0125em] text-foreground">Хэрэглэгчийн сэтгэгдэл</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        {renderSummaryAside}

          <div className="bg-white rounded-2xl border border-border">
            <div className="px-4 sm:px-6 py-3 flex items-center gap-2 border-b border-border">
              <div className="flex items-center gap-2 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {filters.map(f => (
                  <button
                    key={f.id}
                    onClick={() => { setActiveFilter(f.id); setShowAll(false) }}
                    className={classNames(
                      'shrink-0 h-7 px-3 rounded-full text-xs font-medium border transition-colors cursor-pointer',
                      activeFilter === f.id
                        ? 'bg-foreground text-white border-foreground'
                        : 'bg-white text-muted-foreground border-border hover:bg-muted'
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setSortNewest(s => !s)}
                className="ml-auto shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                <ArrowDownUp className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{sortNewest ? 'Хамгийн сүүлийн' : 'Хамгийн эхний'}</span><span className="sm:hidden">{sortNewest ? 'Сүүлд' : 'Эхэнд'}</span>
              </button>
            </div>

            {filteredReviews.length > 0 ? (
              <>
                <div className="divide-y divide-border px-4 sm:px-6">
                  {visibleReviews.map((rev: any) => (
                    <article key={rev.id} className="py-5">
                      <div className="flex items-start gap-3">
                        {rev.customer?.avatar ? (
                          <img src={formatImageURL(rev.customer?.avatar)} alt="profile" className="rounded-full w-10 h-10 object-cover shrink-0" />
                        ) : (
                          <div className="shrink-0">
                            <Avatar size={40} variant="marble" name={rev.customer?.id || undefined} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-sm font-medium text-foreground">
                              {rev.customer ? rev.customer?.firstName : 'Хэрэглэгч'}
                            </div>
                            <span className="text-[11px] text-muted-foreground tabular-nums">
                              {formatDateNormal(new Date(rev.createdAt))}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center gap-0.5">
                            {[0, 1, 2, 3, 4].map((r) => (
                              <Star key={r} className={classNames(rev.rating > r ? `text-yellow-500 fill-yellow-500` : 'fill-muted-foreground/10 text-muted-foreground/10', 'size-3.5 shrink-0')} />
                            ))}
                          </div>
                          <div className="mt-2">
                            {rev.comment ? (
                              <div className="text-sm leading-relaxed text-foreground/80 tracking-[.0125em] blog-detail prose prose-p:mt-0 prose-p:mb-0" dangerouslySetInnerHTML={{ __html: rev.comment }} />
                            ) : (
                              <span className="text-sm leading-relaxed italic text-muted-foreground">Сэтгэгдэл хоосон байна</span>
                            )}
                          </div>
                          {rev.images && rev.images.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {rev.images.map((image: any, idx: number) => (
                                <img key={idx} src={formatImageURL(image)} alt={`Review image ${idx + 1}`} className="w-16 h-16 object-cover rounded cursor-pointer" onClick={() => openImageInCarousel(rev.images || [], image)} />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                {hasMore && (
                  <div className="px-4 sm:px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {visibleReviews.length} / {filteredReviews.length} сэтгэгдэл
                    </span>
                    <Button variant="outline" onClick={() => setShowAll(s => !s)}>
                      {showAll ? 'Хураах' : 'Бүгдийг харах'}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="px-4 sm:px-6 py-10">
                <EmptyState icon={MessageCircleX} title={activeFilter === 'all' ? 'Сэтгэгдэл хоосон байна' : 'Тохирох сэтгэгдэл олдсонгүй'} description={activeFilter === 'all' ? 'Яг одоогоор ямар нэгэн сэтгэгдэл байхгүй байна. Хэсэг хугацааны дараа дахин шалгана уу.' : 'Өөр шүүлтүүр сонгож үзнэ үү.'} />
              </div>
            )}
          </div>
      </div>

      {renderComposerSection}
    </div>
  )
}

function UserReviewStatus({ publishedReview, pendingReview, deletedReview, composerOpen, setComposerOpen, composer, openImageInCarousel }: { publishedReview: any, pendingReview: any, deletedReview: any, composerOpen: boolean, setComposerOpen: (v: boolean) => void, composer: React.ReactNode, openImageInCarousel: (imgs: string[], clicked: string) => void }) {
  return (
    <div className="space-y-3">
      {publishedReview && (
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 grid place-items-center rounded-full bg-green-100 shrink-0">
            <CircleCheckBig className="text-green-600 size-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm font-semibold text-foreground leading-tight">Та амжилттай үнэлгээ өгсөн байна</div>
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                Нийтлэгдсэн
              </span>
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[0, 1, 2, 3, 4].map((r) => (
                  <Star key={r} className={classNames(publishedReview.rating > r ? `text-yellow-500 fill-yellow-500` : 'fill-muted-foreground/10 text-muted-foreground/10', 'size-4 shrink-0')} />
                ))}
              </div>
              {publishedReview.createdAt && (
                <span className="text-[11px] text-muted-foreground tabular-nums">· {formatDateNormal(new Date(publishedReview.createdAt))}</span>
              )}
            </div>
            {publishedReview.comment && (
              <div className="mt-2 text-sm leading-relaxed text-foreground/80 blog-detail prose prose-p:mt-0 prose-p:mb-0" dangerouslySetInnerHTML={{ __html: publishedReview.comment }} />
            )}
            {publishedReview.images && publishedReview.images.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {publishedReview.images.map((image: any, idx: number) => (
                  <img key={idx} src={formatImageURL(image)} alt={`Review image ${idx + 1}`} className="w-16 h-16 object-cover rounded cursor-pointer" onClick={() => openImageInCarousel(publishedReview.images || [], image)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {pendingReview && (
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 grid place-items-center rounded-full bg-amber-100 shrink-0">
            <CircleHelp className="text-amber-600 size-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm font-semibold text-foreground leading-tight">Таны үнэлгээ хянагдаж байна</div>
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                Хянагдаж байна
              </span>
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[0, 1, 2, 3, 4].map((r) => (
                  <Star key={r} className={classNames(pendingReview.rating > r ? `text-yellow-500 fill-yellow-500` : 'fill-muted-foreground/10 text-muted-foreground/10', 'size-4 shrink-0')} />
                ))}
              </div>
              {pendingReview.createdAt && (
                <span className="text-[11px] text-muted-foreground tabular-nums">· {formatDateNormal(new Date(pendingReview.createdAt))}</span>
              )}
            </div>
            {pendingReview.comment && (
              <div className="mt-2 text-sm leading-relaxed text-foreground/80 blog-detail prose prose-p:mt-0 prose-p:mb-0" dangerouslySetInnerHTML={{ __html: pendingReview.comment }} />
            )}
            {pendingReview.images && pendingReview.images.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {pendingReview.images.map((image: any, idx: number) => (
                  <img key={idx} src={formatImageURL(image)} alt={`Review image ${idx + 1}`} className="w-16 h-16 object-cover rounded cursor-pointer" onClick={() => openImageInCarousel(pendingReview.images || [], image)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {deletedReview && !publishedReview && (
        <div>
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 grid place-items-center rounded-full bg-red-100 shrink-0">
              <CircleX className="text-red-600 size-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm font-semibold text-foreground leading-tight">Таны үнэлгээг татгалзсан байна</div>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                  Татгалзсан
                </span>
              </div>
              <p className="text-xs mt-1 text-muted-foreground leading-snug">
                {pendingReview ? 'Засварлагдсан сэтгэгдлийг хянаж байна.' : 'Та сэтгэгдлээ дахин засварлаж илгээх боломжтой.'}
                {deletedReview.createdAt && (
                  <span className="ml-1.5 tabular-nums">· {formatDateNormal(new Date(deletedReview.createdAt))}</span>
                )}
              </p>
            </div>
          </div>
          {!pendingReview && !publishedReview && (
            composerOpen ? (
              <div className="mt-4 pt-4 border-t border-border">
                {composer}
              </div>
            ) : (
              <div className="mt-3 flex justify-end">
                <Button variant="outline" icon={PenLine} onClick={() => setComposerOpen(true)}>
                  Дахин үнэлгээ өгөх
                </Button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}
